/**
 * @module ScopeService
 *
 * Controls feature visibility based on the current user's scopes.
 *
 * ## Key rules
 * - `augintel_admin` users bypass all scope checks (`canViewScope` always
 *   returns `true`, `cantViewScope` always returns `false`).
 * - When `impersonation` is active, the impersonated user's scopes are
 *   used instead of the profile's — even for admins.
 * - A `null` profile always denies access.
 *
 * @example
 * // Show a feature only to users with the 'monitors_enabled_in_elevate' scope
 * if (canViewScope('monitors_enabled_in_elevate', profile, impersonation)) { ... }
 *
 * // Hide a feature from users with the 'queries_disabled_in_elevate' scope
 * if (!cantViewScope('queries_disabled_in_elevate', profile, impersonation)) { ... }
 */
type ScopeProfile = { user_type: string; scopes: string[] } | null;
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
type ScopeImpersonation = { scopes?: string[] } | null | unknown;

export const canViewScope = (
  scope: string,
  profile: ScopeProfile,
  impersonation: ScopeImpersonation): boolean => {
  const imp = impersonation as { scopes?: string[] } | null;
  if (imp)
    return !!imp.scopes && imp.scopes.indexOf(scope) > -1;
  if (!profile)
    return false;
  if (profile.user_type && profile.user_type === 'augintel_admin')
    return true;

  return profile.scopes && profile.scopes.indexOf(scope) > -1;
};

export const cantViewScope = (
  negatedScope: string,
  profile: ScopeProfile,
  impersonation: ScopeImpersonation): boolean => {
  const imp = impersonation as { scopes?: string[] } | null;
  if (!profile)
    return true;
  if (imp)
    return !!imp.scopes && imp.scopes.indexOf(negatedScope) > -1;
  if (profile.user_type && profile.user_type === 'augintel_admin')
    return false;

  return profile.scopes && profile.scopes.indexOf(negatedScope) > -1;
};
